import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 모듈 레벨 전역 상태 — 여러 컴포넌트에서 useUserLocale() 호출해도 동기화됨
let _globalLocale = null;
const _localeListeners = new Set();
const _EVENT_NAME = "newcoupling:currency-change";

function getInitialLocale() {
  if (_globalLocale) return _globalLocale;
  _globalLocale = detectLocale();
  return _globalLocale;
}

function broadcastLocale(next) {
  _globalLocale = next;
  _localeListeners.forEach((fn) => {
    try { fn(next); } catch {}
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(_EVENT_NAME, { detail: next }));
  }
}

/**
 * 사용자 지역 자동 감지 + 표시 통화 결정.
 * 전역 상태이므로 어디서 호출하든 같은 값을 반환하고, setCurrency 호출 시 모든 구독자 갱신.
 */
export function useUserLocale() {
  const [locale, setLocaleState] = useState(() => getInitialLocale());

  useEffect(() => {
    const listener = (next) => setLocaleState(next);
    _localeListeners.add(listener);
    // 첫 렌더 후에도 다시 한 번 감지 (지연 hydrate 대응)
    if (!_globalLocale) {
      const detected = detectLocale();
      _globalLocale = detected;
      setLocaleState(detected);
    }
    return () => { _localeListeners.delete(listener); };
  }, []);

  const setCurrency = (cur) => {
    if (!["KRW", "USD", "CAD"].includes(cur)) return;
    try { localStorage.setItem("currency_pref", cur); } catch {}
    const next = {
      countryCode: cur === "KRW" ? "KR" : cur === "USD" ? "US" : "CA",
      currency: cur,
      isManual: true,
    };
    broadcastLocale(next);
  };

  return { ...locale, setCurrency };
}

function detectLocale() {
  // 1) 사용자 수동 선택 우선
  try {
    const pref = localStorage.getItem("currency_pref");
    if (pref && ["KRW", "USD", "CAD"].includes(pref)) {
      return { countryCode: pref === "KRW" ? "KR" : pref === "USD" ? "US" : "CA",
               currency: pref, isManual: true };
    }
  } catch {}

  // 2) navigator.language
  const lang = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "";
  const lower = lang.toLowerCase();
  if (lower.startsWith("ko")) return { countryCode: "KR", currency: "KRW", isManual: false };
  if (lower === "en-ca" || lower === "fr-ca") return { countryCode: "CA", currency: "CAD", isManual: false };
  if (lower.startsWith("en-us") || lower === "en") {
    // en/en-US 는 timezone 으로 추가 검증
    const tz = getTimeZone();
    if (tz.includes("toronto") || tz.includes("vancouver") || tz.includes("montreal") || tz.includes("edmonton") || tz.includes("halifax") || tz.includes("winnipeg")) {
      return { countryCode: "CA", currency: "CAD", isManual: false };
    }
    if (tz.includes("seoul")) return { countryCode: "KR", currency: "KRW", isManual: false };
    return { countryCode: "US", currency: "USD", isManual: false };
  }

  // 3) timezone fallback
  const tz = getTimeZone();
  if (tz.includes("seoul") || tz.includes("korea")) return { countryCode: "KR", currency: "KRW", isManual: false };
  if (tz.includes("toronto") || tz.includes("vancouver") || tz.includes("montreal") || tz.includes("edmonton") || tz.includes("halifax") || tz.includes("winnipeg") || tz.includes("canada"))
    return { countryCode: "CA", currency: "CAD", isManual: false };
  if (tz.startsWith("america/")) return { countryCode: "US", currency: "USD", isManual: false };

  // 4) Fallback: CAD (결제 통화와 일치)
  return { countryCode: "OTHER", currency: "CAD", isManual: false };
}

function getTimeZone() {
  try {
    return (Intl.DateTimeFormat().resolvedOptions().timeZone || "").toLowerCase();
  } catch {
    return "";
  }
}

// 모듈 캐시 — 환율 (base → multi targets)
const _fxCache = { ts: 0, base: "", rates: {} };
const _inflight = { p: null, key: "" };
const TTL_MS = 60 * 60 * 1000;

const FALLBACK_RATES = {
  CAD: { USD: 0.73, KRW: 1100, CAD: 1 },
  USD: { KRW: 1500, CAD: 1.37, USD: 1 },
  KRW: { USD: 0.00067, CAD: 0.00091, KRW: 1 },
};

async function fetchRates(base) {
  const now = Date.now();
  if (_fxCache.base === base && now - _fxCache.ts < TTL_MS) return _fxCache.rates;
  const key = `rates_${base}`;
  if (_inflight.key === key && _inflight.p) return _inflight.p;
  _inflight.key = key;
  _inflight.p = (async () => {
    try {
      // 한 번에 여러 target 받기
      const targets = base === "CAD" ? "USD,KRW" : base === "USD" ? "CAD,KRW" : "CAD,USD";
      const res = await fetch(`${API_URL}/api/exchange-rates?base=${base}&targets=${targets}`);
      const data = await res.json();
      const rates = { ...(data?.rates || {}), [base]: 1 };
      _fxCache.base = base;
      _fxCache.ts = Date.now();
      _fxCache.rates = rates;
      return rates;
    } catch {
      return FALLBACK_RATES[base] || { [base]: 1 };
    } finally {
      _inflight.key = "";
      _inflight.p = null;
    }
  })();
  return _inflight.p;
}

/**
 * 가격을 자동 감지된 사용자 통화로 포맷팅.
 * base: 가격이 정의된 기준 통화 (PayPal 결제 통화 = CAD)
 *
 * Returns: { displayCurrency, format(amount), ready, baseCurrency }
 */
export function useLocalizedPrice(base = "CAD") {
  const { currency } = useUserLocale();
  const [rates, setRates] = useState(_fxCache.base === base ? _fxCache.rates : (FALLBACK_RATES[base] || { [base]: 1 }));
  const [ready, setReady] = useState(_fxCache.base === base);

  useEffect(() => {
    let mounted = true;
    fetchRates(base).then((r) => {
      if (mounted) { setRates(r); setReady(true); }
    });
    return () => { mounted = false; };
  }, [base]);

  const format = (amount) => {
    const n = Number(amount) || 0;
    const rate = rates?.[currency] ?? FALLBACK_RATES[base]?.[currency] ?? 1;
    const converted = n * rate;
    if (currency === "KRW") {
      const rounded = Math.round(converted);
      return `₩${rounded.toLocaleString("ko-KR")}`;
    }
    if (currency === "USD") {
      return `US$${converted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    // CAD (default)
    return `CA$${converted.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return {
    displayCurrency: currency,
    baseCurrency: base,
    format,
    ready,
    rates,
  };
}

export default useLocalizedPrice;
